/**
 * Data em formato brasileiro na tela, ISO no fio.
 *
 * A separação é deliberada. O `TRY_CONVERT(DATE, …)` do T-SQL, sem estilo,
 * depende do `DATEFORMAT` da sessão: `03/04/1980` vira 3 de abril ou 4 de março
 * conforme a configuração do servidor, e o erro é SILENCIOSO — grava uma data
 * plausível e errada, que ninguém confere. Já `AAAA-MM-DD` é inequívoco para o
 * tipo DATE independentemente de idioma.
 *
 * Então: a pessoa digita e lê DD/MM/AAAA, e a conversão acontece na fronteira.
 */

const MASCARA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const MASCARA_BR = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Ano mínimo aceito. Antes disso é quase certamente erro de digitação. */
const ANO_MINIMO = 1900;

/** Remove tudo que não for dígito e limita a 8 (DDMMAAAA). */
export function normalizarData(valor: string): string {
  return valor.replace(/\D/g, '').slice(0, 8);
}

/**
 * Máscara progressiva para digitação: `1`, `15`, `15/0`, `15/08/19`, …
 *
 * Progressiva porque reformatar só quando o campo está completo faz o cursor
 * saltar no meio da digitação, e num celular isso é o bastante para a pessoa
 * desistir do campo.
 */
export function formatarDataBr(valor: string): string {
  const d = normalizarData(valor);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Data de calendário real — pega 31/02, 31/04 e 29/02 fora de bissexto.
 *
 * A checagem por reconstrução é o que fecha o buraco: `new Date(1980, 1, 31)`
 * não falha, ele ROLA para 2 de março. Comparar os componentes de volta é o
 * único jeito confiável de saber que a data existe.
 */
function ehDataReal(ano: number, mes: number, dia: number): boolean {
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return false;
  const d = new Date(ano, mes - 1, dia);
  return d.getFullYear() === ano && d.getMonth() === mes - 1 && d.getDate() === dia;
}

/**
 * `DD/MM/AAAA` completo e real — sem opinião sobre passado ou futuro.
 *
 * A regra "não pode ser futuro" morava AQUI, e vazava para todo campo de data
 * do sistema: a vigência do limite de crédito, que por definição é futura, era
 * recusada com "Data inválida. Use DD/MM/AAAA." — uma mensagem que acusa o
 * formato quando o formato estava certo. Quem precisa da regra é o nascimento,
 * e só ele: `validarNascimentoBr`.
 */
export function validarDataBr(valor: string): boolean {
  const achado = MASCARA_BR.exec(valor.trim());
  if (!achado) return false;

  const dia = Number(achado[1]);
  const mes = Number(achado[2]);
  const ano = Number(achado[3]);

  if (ano < ANO_MINIMO) return false;
  return ehDataReal(ano, mes, dia);
}

/**
 * Data de nascimento: real e no passado.
 *
 * Nascimento no futuro é sempre erro de digitação. A comparação usa o início do
 * dia de hoje para que a data de hoje continue aceita.
 */
export function validarNascimentoBr(valor: string): boolean {
  if (!validarDataBr(valor)) return false;

  const achado = MASCARA_BR.exec(valor.trim());
  if (!achado) return false;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return new Date(Number(achado[3]), Number(achado[2]) - 1, Number(achado[1])) <= hoje;
}

/** `DD/MM/AAAA` → `AAAA-MM-DD`. Devolve `''` se não for data válida. */
export function brParaIso(valor: string): string {
  if (!validarDataBr(valor)) return '';
  const achado = MASCARA_BR.exec(valor.trim());
  if (!achado) return '';
  return `${achado[3]}-${achado[2]}-${achado[1]}`;
}

/**
 * `AAAA-MM-DD` → `DD/MM/AAAA`. Devolve `''` para entrada vazia ou nula.
 *
 * Faz a troca por regex, sem passar por `Date`: `new Date('1980-08-15')` é lido
 * como UTC e volta um dia em qualquer fuso negativo — o Brasil inteiro.
 */
export function isoParaBr(valor: string | null | undefined): string {
  if (!valor) return '';
  const achado = MASCARA_ISO.exec(valor.trim().slice(0, 10));
  if (!achado) return '';
  return `${achado[3]}/${achado[2]}/${achado[1]}`;
}
